import { useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { LoadingFrame } from "@covertree/ui";
import { useContinueQuote } from "@providers/ContinueQuoteProvider";
import { useContinueQuoteHandler } from "@hooks/actions/useContinueQuoteHandler";
import {
  GetPolicyWithStartDateAfterUwReviewQuery,
  useGetPolicyWithStartDateAfterUwReviewLazyQuery,
} from "@covertree/agent-portal-graphql";
import { QUOTES_PATH } from "@routing/paths";
import { Toast } from "@covertree/utils";
import { useAccessPattern } from "@hooks/useAccessPattern";

export function QuotesContinueLoading() {
  const navigate = useNavigate();
  const location = useLocation();
  const { policyLocator } = useParams();
  const { continueQuoteHandler } = useContinueQuoteHandler();
  const [getPolicy] = useGetPolicyWithStartDateAfterUwReviewLazyQuery();
  const { showContinueQuoteModal } = useContinueQuote();
  const { canWriteBusiness: canContinueQuote } = useAccessPattern();

  const isContinueQuoteLoading = !!location.state?.effectiveDate;

  const handleGetPolicySuccess = useCallback(
    (data: GetPolicyWithStartDateAfterUwReviewQuery) => {
      const startDateAfterUWReview = data?.getPolicy?.startDateAfterUWReview;

      showContinueQuoteModal(policyLocator, startDateAfterUWReview);
      navigate(`/${QUOTES_PATH}`, { replace: true });
    },
    [navigate, policyLocator, showContinueQuoteModal]
  );

  const handleGetPolicyError = useCallback(() => {
    Toast.showError("Policy not found");
    navigate(`/${QUOTES_PATH}`, { replace: true });
  }, [navigate]);

  const loadPolicy = useCallback(async () => {
    if (!policyLocator || !canContinueQuote) {
      navigate(-1);
      return;
    }

    if (isContinueQuoteLoading) {
      await continueQuoteHandler(policyLocator, location.state.effectiveDate);
    } else {
      await getPolicy({
        variables: { policyLocator },
        fetchPolicy: "cache-and-network",
        onCompleted: handleGetPolicySuccess,
        onError: handleGetPolicyError,
      });
    }
  }, [
    policyLocator,
    isContinueQuoteLoading,
    continueQuoteHandler,
    getPolicy,
    handleGetPolicySuccess,
    handleGetPolicyError,
    canContinueQuote,
  ]);

  const messages = useMemo(() => {
    if (!isContinueQuoteLoading) {
      return ["Loading policy..."];
    }
    return [
      "Initiating continue quote process",
      "Fetching selected quote data",
      "Restoring your previous session",
    ];
  }, [isContinueQuoteLoading]);

  useEffect(() => {
    loadPolicy();
  }, [policyLocator, location.state?.effectiveDate]);

  return (
    <LoadingFrame
      isFixed={true}
      image="/animations/spinner.gif"
      messages={messages}
      maxLoadTime={12}
    />
  );
}
